import { useState, useMemo, useCallback } from 'react'
import { Search, ChevronLeft, ChevronRight, ArrowUpDown, Users2 } from 'lucide-react'
import {
  useColaboradoresListEmployees,
  useColaboradoresListDepartments,
  type ListEmployeesInput,
  type EmployeeListItem,
} from '@/api/colaboradores-hooks'

interface EmployeeDataTableProps {
  onSelectEmployee: (employee: EmployeeListItem) => void
}

const PAGE_SIZE = 50

function formatCurrency(value: number | null): string {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleDateString('pt-BR')
  } catch {
    return dateStr
  }
}

const CONTRACT_LABELS: Record<string, string> = {
  CLT: 'CLT',
  PJ: 'PJ',
  ESTAGIO: 'Estágio',
  SOCIO: 'Sócio',
  FREELANCER: 'Freelancer',
}

const STATUS_OPTIONS = [
  { value: 'A', label: 'Ativos' },
  { value: 'D', label: 'Desligados' },
  { value: 'all', label: 'Todos' },
] as const

const SORT_OPTIONS = [
  { value: 'name', label: 'Nome' },
  { value: 'salary', label: 'Salário' },
  { value: 'admissao', label: 'Admissão' },
  { value: 'setor', label: 'Setor' },
] as const

export function EmployeeDataTable({ onSelectEmployee }: EmployeeDataTableProps) {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [status, setStatus] = useState<'A' | 'D' | 'all'>('A')
  const [contractType, setContractType] = useState<string>('')
  const [departmentId, setDepartmentId] = useState<number | undefined>()
  const [orderBy, setOrderBy] = useState<ListEmployeesInput['orderBy']>('name')
  const [page, setPage] = useState(0)

  // Debounce search
  const debounceTimer = useMemo(() => {
    let timer: ReturnType<typeof setTimeout>
    return (value: string) => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        setDebouncedSearch(value)
        setPage(0)
      }, 300)
    }
  }, [])

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearch(value)
    debounceTimer(value)
  }, [debounceTimer])

  const input: ListEmployeesInput = {
    status,
    search: debouncedSearch || undefined,
    contractType: contractType || undefined,
    departmentId,
    orderBy,
    take: PAGE_SIZE,
    skip: page * PAGE_SIZE,
  }

  const { data, isLoading } = useColaboradoresListEmployees(input)
  const { data: departments } = useColaboradoresListDepartments()

  const employees = data?.employees ?? []
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta overflow-hidden shadow-monumenta">
      {/* Filters bar */}
      <div className="p-4 border-b border-brand-dark-lighter space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray" />
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              aria-label="Buscar colaboradores"
              value={search}
              onChange={handleSearchChange}
              className="w-full pl-9 pr-3 py-2 bg-brand-dark/50 border border-brand-dark-lighter rounded-lg text-sm text-white placeholder:text-brand-gray focus:outline-none focus:ring-2 focus:ring-brand-primary/50 transition-colors"
            />
          </div>

          {/* Status filter */}
          <select
            value={status}
            onChange={e => { setStatus(e.target.value as typeof status); setPage(0) }}
            className="px-3 py-2 bg-brand-dark/50 border border-brand-dark-lighter rounded-lg text-sm text-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary/50 transition-colors"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Contract type filter */}
          <select
            value={contractType}
            onChange={e => { setContractType(e.target.value); setPage(0) }}
            className="px-3 py-2 bg-brand-dark/50 border border-brand-dark-lighter rounded-lg text-sm text-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary/50 transition-colors"
          >
            <option value="">Todos contratos</option>
            {Object.entries(CONTRACT_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          {/* Department filter */}
          <select
            value={departmentId ?? ''}
            onChange={e => { setDepartmentId(e.target.value ? Number(e.target.value) : undefined); setPage(0) }}
            className="px-3 py-2 bg-brand-dark/50 border border-brand-dark-lighter rounded-lg text-sm text-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary/50 transition-colors"
          >
            <option value="">Todos setores</option>
            {(departments ?? []).map(dept => (
              <option key={dept.id} value={dept.id}>{dept.nome}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-between text-xs text-brand-gray">
          <span>{total} colaborador{total !== 1 ? 'es' : ''} encontrado{total !== 1 ? 's' : ''}</span>
          <div className="flex items-center gap-1">
            <ArrowUpDown className="h-3 w-3" />
            <select
              value={orderBy}
              onChange={e => setOrderBy(e.target.value as typeof orderBy)}
              className="bg-transparent text-brand-gray text-xs focus:outline-none cursor-pointer"
            >
              {SORT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>Ordenar: {opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-dark-lighter text-brand-gray text-left">
              <th className="px-4 py-3 font-medium">Nome</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Contrato</th>
              <th className="px-4 py-3 font-medium">Setor</th>
              <th className="px-4 py-3 font-medium text-right">Salário</th>
              <th className="px-4 py-3 font-medium">Admissão</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-b border-brand-dark-lighter/50">
                  {Array.from({ length: 7 }).map((_, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 bg-brand-border/20 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : employees.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center">
                  <Users2 className="h-8 w-8 text-brand-gray/50 mx-auto mb-2" />
                  <p className="text-brand-gray">Nenhum colaborador encontrado</p>
                </td>
              </tr>
            ) : (
              employees.map(emp => (
                <tr
                  key={emp.id}
                  tabIndex={0}
                  role="button"
                  onClick={() => onSelectEmployee(emp)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectEmployee(emp) } }}
                  className="border-b border-brand-dark-lighter/50 hover:bg-brand-primary/5 focus-visible:bg-brand-primary/5 focus-visible:outline-none cursor-pointer transition-colors duration-200"
                >
                  <td className="px-4 py-3 text-white font-medium">{emp.funcionario}</td>
                  <td className="px-4 py-3 text-brand-gray-light">{emp.email || '—'}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-primary/10 text-brand-primary">
                      {CONTRACT_LABELS[emp.contrato || ''] || emp.contrato || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-brand-gray-light">{emp.setorNome || '—'}</td>
                  <td className="px-4 py-3 text-right text-white tabular-nums">{formatCurrency(emp.salario)}</td>
                  <td className="px-4 py-3 text-brand-gray-light">{formatDate(emp.admissao)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      emp.situacao === 'A'
                        ? 'bg-green-500/10 text-green-400'
                        : 'bg-red-500/10 text-red-400'
                    }`}>
                      {emp.situacao === 'A' ? 'Ativo' : 'Desligado'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-brand-dark-lighter text-sm text-brand-gray">
          <span>
            Página {page + 1} de {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              aria-label="Página anterior"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="p-1.5 rounded cursor-pointer hover:bg-brand-primary/10 focus-visible:ring-2 focus-visible:ring-brand-primary/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              aria-label="Próxima página"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="p-1.5 rounded cursor-pointer hover:bg-brand-primary/10 focus-visible:ring-2 focus-visible:ring-brand-primary/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
